import dayjs from "dayjs"
import { getSpanDays, getTicketedSlotsByDate, isExperienceDateUnavailable, isMultiDayStartUnavailable } from "utils/experienceAvailability"
import { PREVIEW_FIRST_DAY_SEARCH_DAYS, PREVIEW_SEASON_YEARS_AHEAD, WEEKDAY_NAMES } from "../constants/schedulingConstants"

const DATE_FORMAT = "YYYY-MM-DD"

const NO_AVAILABILITY = { isDateAvailable: () => false, firstAvailableDate: null }

// "mon" or "monday" → 1 (Sunday = 0); undefined for anything else.
const toWeekdayIndex = (day) => {
  const prefix = String(day ?? "").slice(0, 3).toLowerCase()
  if (prefix.length < 3) return undefined
  const index = WEEKDAY_NAMES.findIndex((name) => name.startsWith(prefix))
  return index === -1 ? undefined : index
}

// The wizard holds a recurring season as the single range the guide entered; the booking page gets
// it from the API already repeated each year. Repeat it the same way here, for display only.
const repeatYearly = (ranges) => {
  const lastYear = dayjs().year() + PREVIEW_SEASON_YEARS_AHEAD
  return ranges.flatMap(({ start, end }) =>
    Array.from({ length: Math.max(0, lastYear - dayjs(start).year()) + 1 }, (_, offset) => ({
      start: dayjs(start).add(offset, "year").format(DATE_FORMAT),
      end: dayjs(end).add(offset, "year").format(DATE_FORMAT)
    }))
  )
}

// A ticketed listing: the days with a start time still open for booking (past the cut-off, on the
// listing's clock). A multi-day start date whose trip runs into the guide's time off is left out,
// as on the booking page.
const buildTicketedAvailability = ({ trip, timeSlots, cutOffValue, cutOffUnit, isMultiDay, spanDays }) => {
  const timeOff = new Set((trip?.unavailable_dates || []).map((date) => String(date).slice(0, 10)))
  const slotsByDate = getTicketedSlotsByDate({ ...trip, time_slots: timeSlots, cut_off_value: cutOffValue, cut_off_unit: cutOffUnit })
  const dates = Object.keys(slotsByDate)
    .filter((date) => !isMultiDay || !getSpanDays(date, spanDays).some((day) => timeOff.has(day.format(DATE_FORMAT))))
    .sort()
  const dateSet = new Set(dates)
  return { isDateAvailable: (date) => dateSet.has(dayjs(date).format(DATE_FORMAT)), firstAvailableDate: dates[0] ?? null }
}

// A private listing: a day its schedule runs on, inside a season when it's seasonal, and not past,
// time off or sold out. A multi-day start date also needs every later day of its trip free.
const buildPrivateAvailability = ({ trip, weekdays, isSeasonal, isRecurringYearly, seasonRanges, isMultiDay, spanDays }) => {
  const openWeekdays = new Set(weekdays.map(toWeekdayIndex).filter((index) => index !== undefined))
  if (!openWeekdays.size || (isSeasonal && !seasonRanges.length)) return NO_AVAILABILITY
  const listing = {
    unavailable_dates: trip?.unavailable_dates,
    sold_out_dates: trip?.sold_out_dates,
    unavailable_weekdays: WEEKDAY_NAMES.filter((_, index) => !openWeekdays.has(index)),
    available_date_ranges: isSeasonal ? (isRecurringYearly ? repeatYearly(seasonRanges) : seasonRanges) : []
  }
  const isDateAvailable = (date) =>
    !(isMultiDay ? isMultiDayStartUnavailable(listing, date, spanDays) : isExperienceDateUnavailable(listing, date))
  const today = dayjs().startOf("day")
  const firstDay = Array.from({ length: PREVIEW_FIRST_DAY_SEARCH_DAYS }, (_, offset) => today.add(offset, "day")).find(isDateAvailable)
  return { isDateAvailable, firstAvailableDate: firstDay ? firstDay.format(DATE_FORMAT) : null }
}

/**
 * The days the Preview's Real-Time Availability calendar marks, by the booking page's rules, so the
 * guide sees what guests will be able to pick: { isDateAvailable(date), firstAvailableDate }.
 * Inputs come from the wizard (its latest edits), with the saved listing for time off and sold-out days.
 */
export const buildPreviewAvailability = ({ isTicketed, ...listing }) =>
  isTicketed ? buildTicketedAvailability(listing) : buildPrivateAvailability(listing)
